import { FC, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { getReadinessBadgeColor, getReadinessColor } from '../../../utils/readinessHelpers';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CaretDown,
  Warning,
  Heartbeat,
  XCircle,
  CheckCircle,
} from '@phosphor-icons/react';
import {
  ICoachSessionAthlete,
  AthleteSessionStatus,
  coachSessionsEndpoints,
} from '../../../endpoints/coachSessions';
import { AthleteDrawer } from './AthleteDrawer';
import CustomAvatar from '../../../shared/CustomAvatar/CustomAvatar';
import { addBaseUrl } from '../../../lib/DurationPicker/utils';
import {
  getSportCapabilities,
  getRoleLabel,
} from '@leopart/sport/sport-capabilities';
import styles from './RosterPanel.module.scss';

// ─── RI color helper ─────────────────────────────────────────────────────────

// Soglie RI dal canone @leopart/calc (70/50/30) via readinessHelpers — prima
// 75/60/45 locali, divergenti da mobile e dalle altre viste.
function riColor(ri?: number): string {
  if (ri == null) return '#b5b5b5';
  return getReadinessBadgeColor(getReadinessColor(ri));
}

// ─── Status meta ──────────────────────────────────────────────────────────────

const STATUS_META: Record<
  AthleteSessionStatus,
  { dot: string; label: string; icon: React.ReactNode }
> = {
  available: {
    dot: '#4dd292',
    label: 'Presente',
    icon: <CheckCircle size={12} weight="fill" />,
  },
  differentiated: {
    dot: '#ffd15a',
    label: 'Differenziato',
    icon: <Warning size={12} weight="fill" />,
  },
  injured: {
    dot: '#f05555',
    label: 'Infortunato',
    icon: <Heartbeat size={12} weight="fill" />,
  },
  absent: {
    dot: '#b5b5b5',
    label: 'Assente',
    icon: <XCircle size={12} weight="fill" />,
  },
};

const STATUS_ORDER: AthleteSessionStatus[] = [
  'available',
  'differentiated',
  'injured',
  'absent',
];

// ─── Player card (drag source + quick status toggle) ────────────────────────

const PlayerCard: FC<{
  athlete: ICoachSessionAthlete;
  sport: string | null;
  onDragStart: (id: string) => void;
  onClick: (a: ICoachSessionAthlete) => void;
  onStatusChange: (athleteId: string, status: AthleteSessionStatus) => void;
}> = ({ athlete, sport, onDragStart, onClick, onStatusChange }) => {
  const ri = athlete.recoveryIndex;
  const hasRi = ri != null;
  const color = hasRi ? riColor(ri) : '#e0e0e0';
  const initials = `${(athlete.firstName?.[0] ?? '').toUpperCase()}${(athlete.lastName?.[0] ?? '').toUpperCase()}`.trim() || '?';
  const fullName =
    `${athlete.firstName ?? ''} ${athlete.lastName ?? ''}`.trim() ||
    athlete.athleteId;
  const meta = STATUS_META[athlete.status] ?? STATUS_META.available;
  const roleLabel = athlete.role ? getRoleLabel(sport, athlete.role) : '';

  return (
    <div
      className={styles.playerCard}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('athleteId', athlete.athleteId);
        onDragStart(athlete.athleteId);
      }}
      onClick={() => onClick(athlete)}
      title={fullName}
    >
      <div className={styles.avatarWrap}>
        <CustomAvatar
          name={initials}
          src={addBaseUrl(1, athlete.imageUrl)}
          className={styles.avatar}
        />
        <span
          className={styles.statusDot}
          style={{ background: meta.dot }}
          title={meta.label}
        />
      </div>

      <div className={styles.playerInfo}>
        <div className={styles.playerName}>{fullName}</div>
        {roleLabel && <div className={styles.playerRole}>{roleLabel}</div>}
      </div>

      {hasRi && (
        <div className={styles.riBox} style={{ background: color }}>
          <span className={styles.riValue}>{ri}</span>
          <span className={styles.riLabel}>RI</span>
        </div>
      )}

      {athlete.shirtNumber != null && (
        <span className={styles.shirtNum}>#{athlete.shirtNumber}</span>
      )}

      {/* Quick status toggle: 4 mini-bottoni rivelati su hover. Click su un
          bottone NON propaga (e.stopPropagation) per non aprire il drawer. */}
      <div className={styles.quickActions} onClick={(e) => e.stopPropagation()}>
        {STATUS_ORDER.map((s) => {
          const active = athlete.status === s;
          const m = STATUS_META[s];
          return (
            <button
              key={s}
              type="button"
              className={`${styles.quickAction} ${active ? styles.quickActionActive : ''}`}
              style={active ? { background: m.dot, color: '#fff' } : undefined}
              onClick={() => onStatusChange(athlete.athleteId, s)}
              title={m.label}
              aria-label={`Imposta ${fullName} come ${m.label}`}
            >
              {m.icon}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ─── Section accordion ────────────────────────────────────────────────────────

const Section: FC<{
  title: string;
  count: number;
  icon?: React.ReactNode;
  athletes: ICoachSessionAthlete[];
  sport: string | null;
  defaultOpen?: boolean;
  onDragStart: (id: string) => void;
  onAthleteClick: (a: ICoachSessionAthlete) => void;
  onStatusChange: (athleteId: string, status: AthleteSessionStatus) => void;
  emptyText?: string;
}> = ({
  title,
  count,
  icon,
  athletes,
  sport,
  defaultOpen = true,
  onDragStart,
  onAthleteClick,
  onStatusChange,
  emptyText = 'Nessun atleta',
}) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader} onClick={() => setOpen((o) => !o)}>
        <span className={styles.sectionTitle}>
          {icon}
          {title}
        </span>
        <span className={styles.sectionCount}>
          {count}
          <CaretDown
            size={12}
            className={`${styles.sectionCaret} ${open ? styles.open : ''}`}
          />
        </span>
      </div>
      {open && (
        <div className={styles.sectionBody}>
          {athletes.map((a) => (
            <PlayerCard
              key={a.athleteId}
              athlete={a}
              sport={sport}
              onDragStart={onDragStart}
              onClick={onAthleteClick}
              onStatusChange={onStatusChange}
            />
          ))}
          {athletes.length === 0 && (
            <span className={styles.emptyHint}>{emptyText}</span>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

interface RosterPanelProps {
  athletes: ICoachSessionAthlete[];
  sport: string | null;
}

export const RosterPanel: FC<RosterPanelProps> = ({ athletes, sport }) => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const queryClient = useQueryClient();
  const sportCaps = getSportCapabilities(sport);
  const [roleFilter, setRoleFilter] = useState('');
  const [separateKeepers, setSeparateKeepers] = useState(false);
  const [, setDraggingId] = useState<string | null>(null);
  const [drawerAthlete, setDrawerAthlete] =
    useState<ICoachSessionAthlete | null>(null);

  // Mutation per cambiare status atleta nella sessione (persistente).
  const statusMut = useMutation({
    mutationFn: (vars: { athleteId: string; status: AthleteSessionStatus }) =>
      coachSessionsEndpoints
        .setAthleteStatus(sessionId!, vars)
        .then((r) => r.data),
    onMutate: async (vars) => {
      // Optimistic update: aggiorna immediatamente la cache senza attendere
      // la response del backend, così l'utente vede il cambio status istantaneo.
      await queryClient.cancelQueries({ queryKey: ['coachSession', sessionId] });
      const prev = queryClient.getQueryData<any>(['coachSession', sessionId]);
      queryClient.setQueryData<any>(['coachSession', sessionId], (old: any) => {
        if (!old?.athletes) return old;
        return {
          ...old,
          athletes: old.athletes.map((a: ICoachSessionAthlete) =>
            a.athleteId === vars.athleteId ? { ...a, status: vars.status } : a,
          ),
        };
      });
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(['coachSession', sessionId], ctx.prev);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['coachSession', sessionId] });
    },
  });

  const handleStatusChange = (
    athleteId: string,
    status: AthleteSessionStatus,
  ) => {
    if (!sessionId) return;
    statusMut.mutate({ athleteId, status });
  };

  // Lista ruoli filtri: SE lo sport ha un registry usa quello (label IT,
  // ordine ufficiale), ALTRIMENTI fallback ai ruoli derivati dagli atleti
  // del team (per sport non ancora mappati / dati legacy).
  const roleOptions = useMemo(() => {
    if (sportCaps.roles.length > 0) {
      return sportCaps.roles.map((r) => ({ key: r.key, label: r.label }));
    }
    const set = new Set<string>();
    athletes.forEach((a) => {
      if (a.role) set.add(a.role);
    });
    return Array.from(set)
      .sort()
      .map((k) => ({ key: k, label: k }));
  }, [sportCaps.roles, athletes]);

  const filtered = useMemo(() => {
    if (!roleFilter) return athletes;
    return athletes.filter((a) => a.role === roleFilter);
  }, [athletes, roleFilter]);

  const available = filtered.filter((a) => a.status === 'available');
  const differentiated = filtered.filter((a) => a.status === 'differentiated');
  const injured = filtered.filter((a) => a.status === 'injured');
  const absent = filtered.filter((a) => a.status === 'absent');

  return (
    <div className={styles.panel}>
      <div className={styles.panelHeader}>
        <div className={styles.panelTitle}>
          ROSTER · {athletes.length} atleti
        </div>
        <div className={styles.filterRow}>
          <select
            className={styles.roleSelect}
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="">Tutti i ruoli</option>
            {roleOptions.map((r) => (
              <option key={r.key} value={r.key}>
                {r.label}
              </option>
            ))}
          </select>
          {sportCaps.hasGoalkeepers && (
            <button
              type="button"
              className={styles.keepersToggle}
              onClick={() => setSeparateKeepers((s) => !s)}
              style={{
                background: separateKeepers
                  ? 'rgba(6, 52, 68, 0.08)'
                  : '#f9f9f9',
                borderColor: separateKeepers ? '#063444' : '#e7e7e7',
                color: separateKeepers ? '#063444' : '#4d4d4d',
              }}
            >
              Portieri sep.
            </button>
          )}
        </div>
      </div>

      <div className={styles.panelBody}>
        <Section
          title="DISPONIBILI"
          count={available.length}
          athletes={available}
          sport={sport}
          defaultOpen
          onDragStart={setDraggingId}
          onAthleteClick={setDrawerAthlete}
          onStatusChange={handleStatusChange}
        />
        <Section
          title="DIFFERENZIATI"
          count={differentiated.length}
          icon={<Warning size={12} className={styles.warningIcon} />}
          athletes={differentiated}
          sport={sport}
          defaultOpen={differentiated.length > 0}
          onDragStart={setDraggingId}
          onAthleteClick={setDrawerAthlete}
          onStatusChange={handleStatusChange}
        />
        <Section
          title="INFORTUNATI"
          count={injured.length}
          icon={<Heartbeat size={12} className={styles.injuryIcon} />}
          athletes={injured}
          sport={sport}
          defaultOpen={injured.length > 0}
          onDragStart={setDraggingId}
          onAthleteClick={setDrawerAthlete}
          onStatusChange={handleStatusChange}
        />
        <Section
          title="ASSENTI"
          count={absent.length}
          icon={<XCircle size={12} className={styles.absentIcon} />}
          athletes={absent}
          sport={sport}
          defaultOpen={absent.length > 0}
          onDragStart={setDraggingId}
          onAthleteClick={setDrawerAthlete}
          onStatusChange={handleStatusChange}
        />
      </div>

      {drawerAthlete && (
        <AthleteDrawer
          athlete={drawerAthlete}
          onClose={() => setDrawerAthlete(null)}
        />
      )}
    </div>
  );
};
